import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { PageTitle } from 'ui/PageTitle';
import { Table } from 'ui/Table';
import { Pagination } from 'pureUi/Pagination';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';
import { usePagination } from 'hooks/usePagination';
import { ESortOrder } from 'store/common/types';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import { useAnnouncementsList, useDeleteAnnouncement } from './hooks/useAnnouncementQueries';
import { formatDateDisplay } from 'utils/date';
import { CircleIconButton } from 'ui/CircleIconButton';
import { HidingTooltip } from 'ui/Tooltip/HidingTooltip';
import PencilIcon from 'ui/Icons/pencil-no-circle.component.svg';
import TrashIcon from 'ui/Icons/tabler/trash.component.svg';
import PlusIcon from 'ui/Icons/plus.component.svg';
import { TextInput } from 'ui/TextInput';
import FluidButton from 'ui/FluidButton';
import { SvgIcon } from 'ui/SvgIcon';
import { FormLabel } from 'ui/FormLabel';
import { useDebounce } from 'hooks/useDebounce';
import { useModal } from 'hooks/useModal';
import { ConfirmationModal, EConfirmationModalType } from 'ui/ConfirmationModal';
import { useNotifications } from 'hooks/useNotifications';
import { Multiselect } from 'ui/Multiselect';
import { getBootstrapHotelsSelector } from 'store/modules/bootstrap/selectors';
import { IBootstrapHotel } from 'store/modules/bootstrap/model';
import { isAdmin } from 'store/modules/auth/selectors';

// Filter interface
interface IAnnouncementFilters {
  title: string;
  hotelUuid: string | null;
}

// Status indicator component with colored dots
const StatusIndicator = ({ status }: { status: string }) => {
  const colorMap: Record<string, string> = {
    Draft: 'bg-bright-sky-blue',
    Active: 'bg-status-confirmed',
    Scheduled: 'bg-status-requested',
    Expired: 'bg-status-discarded',
  };

  return (
    <span className="flex items-center justify-between w-full">
      <span className="italic">{status}</span>
      <span className={`w-3 h-3 rounded-full ${colorMap[status] || 'bg-gray-40'}`} />
    </span>
  );
};

// Table columns configuration
const tableColumns: ITableColumn[] = [
  { id: 'title', label: 'Title', width: 'auto', fieldToSortBy: 'title' },
  { id: 'hotel', label: 'Hotel', width: 'auto', fieldToSortBy: 'hotel' },
  { id: 'createdBy', label: 'Created by', width: 'auto', fieldToSortBy: 'createdBy' },
  { id: 'created', label: 'Created', width: '150px', fieldToSortBy: 'createdAt' },
  { id: 'modified', label: 'Modified', width: '150px', fieldToSortBy: 'updatedAt' },
  { id: 'status', label: 'Status', width: '120px', fieldToSortBy: 'status' },
  { id: 'actions', label: '', width: '180px' },
];

export const AnnouncementsList: React.FC = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const deleteModal = useModal();
  const [itemToDelete, setItemToDelete] = React.useState<string | null>(null);
  const isAdminUser = useSelector(isAdmin);

  // Pagination state (1-indexed to match API)
  const paginationData = usePagination({
    initialPage: 1,
    initialPerPage: 10,
    initialTotal: 0,
  });

  // Sort state (default: newest first)
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<ESortOrder>(ESortOrder.DESC);

  // Filter state
  const [filtersState, setFiltersState] = useState<IAnnouncementFilters>({
    title: '',
    hotelUuid: null,
  });

  // Wrapper function that spreads updates and resets pagination
  const setFilters = (updates: Partial<IAnnouncementFilters>) => {
    setFiltersState(prev => ({ ...prev, ...updates }));
    paginationData.setPage(1);
  };

  // Local title state with debouncing (minimum 3 characters)
  const [localTitle, setLocalTitle, debouncedLocalTitle] = useDebounce('', 1000);

  useEffect(() => {
    if (debouncedLocalTitle.length === 0) {
      setFilters({ title: '' });
    } else if (debouncedLocalTitle.length >= 3 && debouncedLocalTitle !== filtersState.title) {
      setFilters({ title: debouncedLocalTitle });
    }
  }, [debouncedLocalTitle]);

  // Hotel search state for dropdown
  const [selectedHotelUuid, setSelectedHotelUuid] = useState<string | null>(null);

  const bootstrapHotels = useSelector(getBootstrapHotelsSelector) as IBootstrapHotel[];

  const hotelOptions = useMemo(
    () =>
      bootstrapHotels?.map(hotel => ({
        value: hotel.uuid,
        label: hotel.name,
      })) || [],
    [bootstrapHotels]
  );

  // Fetch announcements list with filters
  const { data, isLoading, isError, isFetching } = useAnnouncementsList(
    paginationData.page,
    paginationData.perPage,
    sortBy,
    sortOrder,
    {
      title: filtersState.title || undefined,
      hotelUuid: filtersState.hotelUuid || undefined,
    }
  );

  // Update total when data loads
  useEffect(() => {
    if (data?.meta.total !== undefined) {
      paginationData.setTotal(data.meta.total);
    }
  }, [data]);

  // REQUIRED: Cleanup queries on unmount
  useEffect(() => {
    return () => {
      queryClient.resetQueries({ queryKey: ['announcements', 'list'] });
    };
  }, [queryClient]);

  const handleEdit = (uuid: string) => {
    history.push(`/announcements/${uuid}/edit`);
  };

  // Handle sorting
  const handleSort = (newSortBy: string, newSortOrder: string) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder as ESortOrder);
  };

  // Handle create announcement button
  const handleCreateAnnouncement = () => {
    history.push('/announcements/create');
  };

  // Delete mutation
  const deleteAnnouncementMutation = useDeleteAnnouncement();

  // Handle delete click
  const handleDeleteClick = (uuid: string) => {
    setItemToDelete(uuid);
    deleteModal.showModal();
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await deleteAnnouncementMutation.mutateAsync(itemToDelete);
      showSuccessNotification('Announcement deleted.');
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['announcements', 'list'] });
      deleteModal.handleClose();
      setItemToDelete(null);
    } catch (error) {
      showErrorNotification('Failed to delete announcement');
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    deleteModal.handleClose();
    setItemToDelete(null);
  };

  // Handle hotel selection
  const handleHotelSelect = (hotelUuid: string | null) => {
    setSelectedHotelUuid(hotelUuid);
    setFilters({ hotelUuid });
  };

  // Helper to show validation message for title filter
  const showTitleValidation = localTitle.length > 0 && localTitle.length < 3;

  // Transform API data to table rows
  const items = data?.data || [];
  const tableRows: ITableRow[] = useMemo(
    () =>
      items.map(item => {
        const status = item.status;

        return {
          id: item.uuid,
          cells: [
            { id: 'title', value: item.title },
            { id: 'hotel', value: item.hotel },
            { id: 'createdBy', value: item.createdBy?.firstName + ' ' + item.createdBy?.lastName },
            {
              id: 'created',
              value: item.createdAt ? formatDateDisplay(item.createdAt) : '-',
            },
            {
              id: 'modified',
              value: item.updatedAt ? formatDateDisplay(item.updatedAt) : '-',
            },
            { id: 'status', value: <StatusIndicator status={status} /> },
            {
              id: 'actions',
              value: (
                <div className="flex items-center gap-15px ml-5px">
                  <div className="w-4px h-30px rounded bg-gray-10" />
                  {
                    isAdminUser && <HidingTooltip position="top" renderTooltipContent={() => <span className="p-5px">Edit</span>}>
                      <CircleIconButton
                        type="secondary"
                        IconComponent={PencilIcon}
                        className="fill-brown-prime"
                        onClick={() => handleEdit(item.uuid)}
                      />
                    </HidingTooltip>
                  }
                  <HidingTooltip position="top" renderTooltipContent={() => <span className="p-5px">Remove</span>}>
                    <CircleIconButton
                      type="secondary"
                      IconComponent={TrashIcon}
                      onClick={() => handleDeleteClick(item.uuid)}
                    />
                  </HidingTooltip>
                </div>
              ),
            },
          ],
        };
      }),
    [items, history]
  );

  // Determine if we have initial data
  const hasInitialData = Boolean(data);
  const total = data?.meta.total || 0;

  // Results count text
  const getResultsText = (): string => {
    if (total === 0) return 'no results found';
    if (total === 1) return '1 result found';
    return `${total} results found`;
  };

  return (
    <div className="announcements-listing w-auto mx-[20px] crm-desktop:mx-[80px]">
      <PageTitle
        primaryText="Announcements"
        secondaryCondition={hasInitialData && !isLoading}
        secondaryText={`(${getResultsText()})`}
        loadingCondition={isFetching}
        loadingText="Loading ..."
      />

      {/* Filters */}
      <div className="w-full flex flex-col gap-15px mb-20px">
        {/* Title */}
        <span className="font-pt-sans font-bold text-17px text-text-colour">Filter Results</span>

        {/* Row 1 - Main filters */}
        <div className="flex items-stretch gap-20px w-full">
          {/* Title Filter */}
          <div className="flex-1 flex flex-col">
            <FormLabel text="Title" className="mb-5px text-13px tracking-wider">
              <TextInput
                id="filter-title"
                value={localTitle}
                onChange={e => {
                  if (e.currentTarget.value.length === 0) {
                    setFilters({ title: '' });
                  }
                  setLocalTitle(e.currentTarget.value);
                }}
                placeholder="Search by title..."
                inputClassName="h-35px mt-0"
              />
              {showTitleValidation && (
                <p className="text-red-95 text-13px leading-2xs font-pt-sans mt-5px">
                  Please enter at least 3 characters
                </p>
              )}
            </FormLabel>
          </div>

          {/* Hotel Filter */}
          <div className="flex-1 flex flex-col">
            <span className="font-pt-sans block mb-5px text-13px tracking-wider text-black">Hotel</span>
            <Multiselect
              className="max-h-33px bg-ivory font-pt-sans"
              placeholderText="Select Hotel"
              borderColourClass="border-gray-40"
              itemsClassname="bg-ivory"
              itemCtaClassName="hover:bg-teal-20 text-left"
              height={33}
              itemsToDisplayBeforeScroll={10}
              isSingleSelectMode={true}
              isCloseOnSelect={true}
              isEnableFuzzySearch={true}
              hideNonMatchingOptions={true}
              hideCheckboxes={true}
              options={hotelOptions}
              selectedValues={selectedHotelUuid ? [selectedHotelUuid] : []}
              onUpdate={values => {
                handleHotelSelect(values[0] || null);
              }}
            />
          </div>
        </div>

        {/* Row 2 - Action Buttons */}
        <div className="flex flex-row-reverse justify-between items-center gap-20px w-full">
          <div className="flex items-end gap-10px">
            {isAdminUser && (
              <FluidButton type="primary" onClick={handleCreateAnnouncement} textClassName="flex items-center gap-8px">
                <SvgIcon IconComponent={PlusIcon} className="w-12px h-12px fill-white-true" />
                <span>New Announcement</span>
              </FluidButton>
            )}
          </div>
        </div>
      </div>

      {/* Error state */}
      {isError && <ErrorBar />}

      {/* Initial loading state */}
      {!isError && !hasInitialData && <LoadingBar />}

      {/* Table and pagination (only after initial data loads) */}
      {!isError && hasInitialData && (
        <>
          <Table
            columns={tableColumns}
            rows={tableRows}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={isFetching}
            messageWhenEmpty="No results"
            className="mt-5 mb-[20px]"
            evenColor="ivory"
          />

          {/* Only show pagination when we have data */}
          {tableRows.length >= 1 && (
            <Pagination
              currentPage={paginationData.page}
              itemsPerPage={paginationData.perPage}
              onItemsPerPageChange={paginationData.setPerPage}
              onPageSelect={paginationData.setPage}
              pageCount={paginationData.pageCount}
            />
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        type={EConfirmationModalType.WARNING}
        isOpen={deleteModal.isOpen}
        title="You are going to delete an announcement"
        message="Are you sure you want to delete this announcement"
        confirmButtonLabel="Yes"
        cancelButtonLabel="No"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isConfirmLoading={deleteAnnouncementMutation.isPending}
      />
    </div>
  );
};
